import { aspectRatioLabel } from "./aspect.ts";
import { displayValue, formatDuration, formatResolution, formatTimestamp, formatUsd } from "./format.ts";

export interface DetailRow {
  label: string;
  value: string;
}

export interface ReferenceValue {
  asset_id?: string;
  type?: string;
  mime?: string;
  storage_key?: string;
  thumbnail_storage_key?: string | null;
  metadata?: Record<string, unknown>;
}

export interface ReferenceEntry {
  label: string;
  reference: ReferenceValue;
}

interface JobLike {
  model_key: string;
  capability_key: string;
  state: string;
  params: Record<string, unknown>;
  settled_charge_usd?: string | null;
  estimated_charge_usd?: string | null;
  billing_status?: string | null;
  queued_at?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
}

interface AssetLike {
  metadata?: Record<string, unknown> | null;
}

const REFERENCE_FIELDS: readonly (readonly [string, string])[] = [
  ["image", "Image"],
  ["end_image", "End image"],
  ["source_video", "Source video"],
  ["reference_images", "Reference image"],
  ["reference_videos", "Reference video"],
  ["reference_audios", "Reference audio"],
];

export function promptOf(job: Pick<JobLike, "params">): string {
  const text = (key: string) => typeof job.params[key] === "string" ? (job.params[key] as string).trim() : "";
  const fields = ["subject", "action", "performance", "dialogue", "environment", "camera", "lighting"];
  const structured = fields.flatMap((key) => {
    const value = key === "camera" ? text(key).replace(/^(?:camera\s*:\s*)+/i, "").trim() : text(key);
    return value ? [`${key[0].toUpperCase()}${key.slice(1)}: ${value}`] : [];
  });
  return [text("prompt"), structured.join("\n")].filter(Boolean).join("\n\n");
}

export function jobSpecRows(job: JobLike, asset?: AssetLike | null): DetailRow[] {
  const metadata = asset?.metadata ?? {};
  const candidates: [string, unknown][] = [
    ["Model", job.model_key],
    ["Capability", job.capability_key],
    ["Aspect ratio", aspectRatioLabel(asset ?? null, job.params)],
    [
      "Resolution",
      formatResolution(metadata.width, metadata.height) ??
        job.params.resolution ??
        job.params.image_size ??
        job.params.size,
    ],
    ["Duration", formatDuration(metadata.duration_sec) ?? job.params.duration],
    ["Seed", job.params.seed],
    ["Negative prompt", job.params.negative_prompt],
  ];
  return toRows(candidates);
}

export function jobMetaRows(job: JobLike): DetailRow[] {
  const settled = formatUsd(job.settled_charge_usd);
  const estimate = formatUsd(job.estimated_charge_usd);
  const cost = settled ? `${settled} settled` : job.billing_status === "reconciliation_required"
    ? `Usage verification pending${estimate ? ` · ${estimate} reserved maximum` : ""}`
    : estimate ? `${estimate} estimated` : null;
  const candidates: [string, unknown][] = [
    ["Cost", cost],
    ["State", job.state],
    ["Created", formatTimestamp(job.queued_at) ?? formatTimestamp(job.created_at)],
    ["Completed", formatTimestamp(job.completed_at)],
  ];
  return toRows(candidates);
}

export function jobReferences(params: Record<string, unknown>): ReferenceEntry[] {
  return REFERENCE_FIELDS.flatMap(([key, label]) =>
    asReferenceValues(params[key]).map((reference) => ({ label, reference }))
  );
}

export function labelForSlot(value: unknown): string {
  if (typeof value !== "string" || !value) return "Creative direction";
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function toRows(candidates: [string, unknown][]): DetailRow[] {
  return candidates
    .map(([label, value]) => [label, displayValue(value)] as const)
    .filter((entry): entry is [string, string] => entry[1] !== null)
    .map(([label, value]) => ({ label, value }));
}

function asReferenceValues(value: unknown): ReferenceValue[] {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  return values.filter((item): item is ReferenceValue => Boolean(item && typeof item === "object"));
}
